import Add from '@mui/icons-material/Add';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import {
  getDashboardErrorMessage,
  useCreateDashboardVisualizationMutation,
  useDeleteDashboardVisualizationMutation,
  useReorderDashboardVisualizationsMutation,
  useReplaceDashboardVisualizationSeriesMutation,
  useUpdateDashboardVisualizationMutation
} from './dashboardApi';
import DashboardColourPicker from './DashboardColourPicker';
import { DASHBOARD_Y_AXIS } from './dashboardContract';
import {
  dashboardVisualizationToForm,
  DASHBOARD_VISUALIZATION_MAX_SERIES,
  DASHBOARD_VISUALIZATION_TYPE_OPTIONS,
  DASHBOARD_Y_AXIS_OPTIONS,
  EMPTY_DASHBOARD_VISUALIZATION_FORM,
  getActiveDashboardMetrics,
  moveDashboardVisualization,
  moveDashboardVisualizationSeries,
  sortDashboardVisualizations,
  toggleDashboardVisualizationMetric,
  validateDashboardVisualization
} from './dashboardVisualizationModel';

const visualizationTypeLabel = (value) =>
  DASHBOARD_VISUALIZATION_TYPE_OPTIONS.find((option) => option.value === value)
    ?.label ?? value;

const GraphSeriesFields = ({
  activeMetrics,
  errors,
  form,
  onMove,
  onSeriesChange,
  onToggleMetric
}) => {
  const metricById = useMemo(
    () =>
      new Map(
        activeMetrics.map((metric) => [metric.metricDefinitionId, metric])
      ),
    [activeMetrics]
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography fontWeight={700}>Graph metrics</Typography>
        <Typography color="text.secondary" variant="body2">
          Choose one or more active metrics. Each metric can appear once.
        </Typography>
      </Box>

      {activeMetrics.length ? (
        <FormControl error={Boolean(errors.series)} component="fieldset">
          <FormGroup
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))'
              }
            }}
          >
            {activeMetrics.map((metric) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.series.some(
                      (series) =>
                        series.metricDefinitionId === metric.metricDefinitionId
                    )}
                    disabled={
                      form.series.length >=
                        DASHBOARD_VISUALIZATION_MAX_SERIES &&
                      !form.series.some(
                        (series) =>
                          series.metricDefinitionId ===
                          metric.metricDefinitionId
                      )
                    }
                    onChange={(event) =>
                      onToggleMetric(
                        metric.metricDefinitionId,
                        event.target.checked
                      )
                    }
                  />
                }
                key={metric.metricDefinitionId}
                label={metric.label}
              />
            ))}
          </FormGroup>
          <FormHelperText>
            {errors.series ||
              `${form.series.length} of ${DASHBOARD_VISUALIZATION_MAX_SERIES} series selected. Series are drawn in the order shown below.`}
          </FormHelperText>
        </FormControl>
      ) : (
        <Alert severity="warning">
          Add or restore an active metric before configuring a graph.
        </Alert>
      )}

      {form.series.length > 0 && (
        <Stack spacing={1.25}>
          <Typography fontWeight={700} variant="body2">
            Selected series
          </Typography>
          {form.series.map((series, index) => {
            const metric = metricById.get(series.metricDefinitionId);
            const label = metric?.label ?? 'Unavailable metric';
            return (
              <Paper
                elevation={0}
                key={series.metricDefinitionId}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1.5
                }}
              >
                <Stack
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                >
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <Typography fontWeight={700} variant="body2">
                      {label}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      Series {index + 1}
                    </Typography>
                  </Box>
                  <TextField
                    error={Boolean(errors[`series.${index}.yAxis`])}
                    label={`${label} axis`}
                    onChange={(event) =>
                      onSeriesChange(index, 'yAxis', event.target.value)
                    }
                    select
                    size="small"
                    sx={{ minWidth: 145 }}
                    value={series.yAxis}
                  >
                    {DASHBOARD_Y_AXIS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <DashboardColourPicker
                    emptyLabel="Use metric colour"
                    error={Boolean(errors[`series.${index}.colour`])}
                    fallbackColour={metric?.colour}
                    helperText={
                      errors[`series.${index}.colour`] ||
                      'Blank uses metric colour.'
                    }
                    label={`${label} colour`}
                    onChange={(value) => onSeriesChange(index, 'colour', value)}
                    size="small"
                    sx={{ minWidth: 190 }}
                    value={series.colour}
                  />
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title="Move series up">
                      <span>
                        <IconButton
                          aria-label={`Move ${label} series up`}
                          disabled={index === 0}
                          onClick={() => onMove(series.metricDefinitionId, -1)}
                          size="small"
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move series down">
                      <span>
                        <IconButton
                          aria-label={`Move ${label} series down`}
                          disabled={index === form.series.length - 1}
                          onClick={() => onMove(series.metricDefinitionId, 1)}
                          size="small"
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      color="error"
                      onClick={() =>
                        onToggleMetric(series.metricDefinitionId, false)
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const GraphFields = ({
  activeMetrics,
  errors,
  form,
  onChange,
  onMoveSeries,
  onSeriesChange,
  onToggleMetric
}) => (
  <Stack spacing={2.5}>
    <TextField
      autoFocus
      error={Boolean(errors.name)}
      fullWidth
      helperText={errors.name || `${form.name.length}/100 characters`}
      inputProps={{ maxLength: 101 }}
      label="Graph name"
      onChange={(event) => onChange('name', event.target.value)}
      required
      value={form.name}
    />
    <TextField
      error={Boolean(errors.description)}
      fullWidth
      helperText={
        errors.description || `${form.description.length}/500 characters`
      }
      inputProps={{ maxLength: 501 }}
      label="Description"
      minRows={2}
      multiline
      onChange={(event) => onChange('description', event.target.value)}
      value={form.description}
    />
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
      }}
    >
      <TextField
        error={Boolean(errors.visualizationType)}
        helperText={errors.visualizationType}
        label="Graph type"
        onChange={(event) => onChange('visualizationType', event.target.value)}
        required
        select
        value={form.visualizationType}
      >
        {DASHBOARD_VISUALIZATION_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        error={Boolean(errors.xAxisLabel)}
        helperText={
          errors.xAxisLabel || 'Optional; dates are used automatically.'
        }
        inputProps={{ maxLength: 101 }}
        label="X-axis label"
        onChange={(event) => onChange('xAxisLabel', event.target.value)}
        value={form.xAxisLabel}
      />
      <TextField
        error={Boolean(errors.leftYAxisLabel)}
        helperText={errors.leftYAxisLabel || 'Optional.'}
        inputProps={{ maxLength: 101 }}
        label="Left y-axis label"
        onChange={(event) => onChange('leftYAxisLabel', event.target.value)}
        value={form.leftYAxisLabel}
      />
      <TextField
        error={Boolean(errors.rightYAxisLabel)}
        helperText={errors.rightYAxisLabel || 'Optional.'}
        inputProps={{ maxLength: 101 }}
        label="Right y-axis label"
        onChange={(event) => onChange('rightYAxisLabel', event.target.value)}
        value={form.rightYAxisLabel}
      />
    </Box>
    <FormControlLabel
      control={
        <Checkbox
          checked={form.showLegend}
          onChange={(event) => onChange('showLegend', event.target.checked)}
        />
      }
      label="Show graph legend"
    />
    <GraphSeriesFields
      activeMetrics={activeMetrics}
      errors={errors}
      form={form}
      onMove={onMoveSeries}
      onSeriesChange={onSeriesChange}
      onToggleMetric={onToggleMetric}
    />
  </Stack>
);

const GraphRow = ({
  canEdit,
  disableMoveDown,
  disableMoveUp,
  isBusy,
  onDelete,
  onEdit,
  onMoveDown,
  onMoveUp,
  visualization
}) => {
  const orderedSeries = [...(visualization.series ?? [])].sort(
    (left, right) => left.seriesOrder - right.seriesOrder
  );
  const hasRightAxis = orderedSeries.some(
    (series) => series.yAxis === DASHBOARD_Y_AXIS.RIGHT
  );

  return (
    <Paper
      data-testid={`dashboard-visualization-definition-${visualization.dashboardVisualizationId}`}
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography fontWeight={700}>{visualization.name}</Typography>
          {visualization.description && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
              {visualization.description}
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
            <Chip
              label={visualizationTypeLabel(visualization.visualizationType)}
              size="small"
            />
            <Chip
              color={orderedSeries.length ? 'default' : 'error'}
              label={`${orderedSeries.length} ${
                orderedSeries.length === 1 ? 'metric' : 'metrics'
              }`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={
                visualization.showLegend ? 'Legend shown' : 'Legend hidden'
              }
              size="small"
              variant="outlined"
            />
            {hasRightAxis && (
              <Chip label="Dual axes" size="small" variant="outlined" />
            )}
          </Stack>
          {orderedSeries.length > 0 && (
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
              {orderedSeries.map((series) => series.label).join(' · ')}
            </Typography>
          )}
        </Box>

        {canEdit && (
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
            <Tooltip title="Move graph up">
              <span>
                <IconButton
                  aria-label={`Move ${visualization.name} up`}
                  disabled={disableMoveUp || isBusy}
                  onClick={onMoveUp}
                  size="small"
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move graph down">
              <span>
                <IconButton
                  aria-label={`Move ${visualization.name} down`}
                  disabled={disableMoveDown || isBusy}
                  onClick={onMoveDown}
                  size="small"
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              disabled={isBusy}
              onClick={onEdit}
              size="small"
              startIcon={<EditOutlined />}
            >
              Edit
            </Button>
            <Button
              color="error"
              disabled={isBusy}
              onClick={onDelete}
              size="small"
              startIcon={<DeleteOutline />}
            >
              Remove
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const DashboardVisualizationDesigner = ({
  canEdit,
  dashboardTemplateId,
  draftVersion
}) => {
  const activeMetrics = getActiveDashboardMetrics(draftVersion?.metrics);
  const visualizations = sortDashboardVisualizations(
    draftVersion?.visualizations
  );
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedVisualization, setSelectedVisualization] = useState(null);
  const [deleteVisualization, setDeleteVisualization] = useState(null);
  const [form, setForm] = useState({
    ...EMPTY_DASHBOARD_VISUALIZATION_FORM,
    series: []
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [createVisualization, createState] =
    useCreateDashboardVisualizationMutation();
  const [updateVisualization, updateState] =
    useUpdateDashboardVisualizationMutation();
  const [replaceSeries, seriesState] =
    useReplaceDashboardVisualizationSeriesMutation();
  const [removeVisualization, deleteState] =
    useDeleteDashboardVisualizationMutation();
  const [reorderVisualizations, reorderState] =
    useReorderDashboardVisualizationsMutation();
  const editorBusy =
    createState.isLoading || updateState.isLoading || seriesState.isLoading;
  const isBusy = editorBusy || deleteState.isLoading || reorderState.isLoading;
  const actionError = deleteState.error || reorderState.error;

  const clearActionStates = () => {
    deleteState.reset?.();
    reorderState.reset?.();
    setSuccessMessage(null);
  };

  const openCreate = () => {
    createState.reset?.();
    updateState.reset?.();
    seriesState.reset?.();
    setSelectedVisualization(null);
    setForm({ ...EMPTY_DASHBOARD_VISUALIZATION_FORM, series: [] });
    setErrors({});
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const openEdit = (visualization) => {
    createState.reset?.();
    updateState.reset?.();
    seriesState.reset?.();
    setSelectedVisualization(visualization);
    setForm(dashboardVisualizationToForm(visualization));
    setErrors({});
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const setFormValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const toggleMetric = (metricDefinitionId, selected) => {
    setForm((current) => ({
      ...current,
      series: toggleDashboardVisualizationMetric(
        current.series,
        metricDefinitionId,
        selected
      )
    }));
    setErrors((current) => {
      if (!current.series) return current;
      const next = { ...current };
      delete next.series;
      return next;
    });
  };

  const setSeriesValue = (index, field, value) => {
    setForm((current) => ({
      ...current,
      series: current.series.map((series, seriesIndex) =>
        seriesIndex === index ? { ...series, [field]: value } : series
      )
    }));
    setErrors((current) => {
      const key = `series.${index}.${field}`;
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const moveSeries = (metricDefinitionId, direction) => {
    setForm((current) => ({
      ...current,
      series: moveDashboardVisualizationSeries(
        current.series,
        metricDefinitionId,
        direction
      )
    }));
  };

  const submitVisualization = async (event) => {
    event.preventDefault();
    const validation = validateDashboardVisualization(form, activeMetrics);
    setErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      if (selectedVisualization) {
        const identifiers = {
          dashboardTemplateId,
          dashboardVisualizationId:
            selectedVisualization.dashboardVisualizationId
        };
        await updateVisualization({
          ...identifiers,
          ...validation.values.configuration
        }).unwrap();
        await replaceSeries({
          ...identifiers,
          series: validation.values.series
        }).unwrap();
        setSuccessMessage(`${validation.values.configuration.name} updated.`);
      } else {
        await createVisualization({
          dashboardTemplateId,
          ...validation.values.configuration,
          series: validation.values.series
        }).unwrap();
        setSuccessMessage(`${validation.values.configuration.name} added.`);
      }
      setEditorOpen(false);
    } catch (_error) {
      // RTK Query exposes normalized errors through the mutation states.
    }
  };

  const moveVisualization = async (dashboardVisualizationId, direction) => {
    clearActionStates();
    try {
      await reorderVisualizations({
        dashboardTemplateId,
        dashboardVisualizationIds: moveDashboardVisualization(
          visualizations,
          dashboardVisualizationId,
          direction
        )
      }).unwrap();
      setSuccessMessage('Graph order updated.');
    } catch (_error) {
      // RTK Query exposes the normalized error through reorderState.
    }
  };

  const confirmDelete = async () => {
    if (!deleteVisualization) return;
    clearActionStates();
    try {
      await removeVisualization({
        dashboardTemplateId,
        dashboardVisualizationId: deleteVisualization.dashboardVisualizationId
      }).unwrap();
      setSuccessMessage(`${deleteVisualization.name} removed.`);
      setDeleteVisualization(null);
    } catch (_error) {
      // Keep the confirmation open so the API error remains visible.
    }
  };

  return (
    <Box data-testid="dashboard-visualization-designer" sx={{ mt: 4 }}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h2" fontSize={22} fontWeight={700}>
            Dashboard graphs
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Build line or bar graphs from active metrics and configure their
            series, axes, colours, legend, and display order.
          </Typography>
        </Box>
        {canEdit && (
          <Tooltip
            title={
              activeMetrics.length
                ? ''
                : 'Add or restore an active metric first.'
            }
          >
            <span>
              <Button
                disabled={!activeMetrics.length}
                onClick={openCreate}
                startIcon={<Add />}
                variant="contained"
              >
                Add graph
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Typography color="text.secondary" sx={{ mt: 1.5 }} variant="body2">
        {visualizations.length}{' '}
        {visualizations.length === 1 ? 'graph' : 'graphs'} ·{' '}
        {activeMetrics.length} active{' '}
        {activeMetrics.length === 1 ? 'metric' : 'metrics'} available
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You can view this graph configuration, but you do not have permission
          to change it.
        </Alert>
      )}
      {!activeMetrics.length && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Graphs need at least one active metric. Add or restore a metric before
          creating or repairing graph series.
        </Alert>
      )}
      {successMessage && (
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ mt: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(actionError)}
        </Alert>
      )}

      {visualizations.length ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {visualizations.map((visualization, index) => (
            <GraphRow
              canEdit={canEdit}
              disableMoveDown={index === visualizations.length - 1}
              disableMoveUp={index === 0}
              isBusy={isBusy}
              key={visualization.dashboardVisualizationId}
              onDelete={() => {
                clearActionStates();
                setDeleteVisualization(visualization);
              }}
              onEdit={() => openEdit(visualization)}
              onMoveDown={() =>
                moveVisualization(visualization.dashboardVisualizationId, 1)
              }
              onMoveUp={() =>
                moveVisualization(visualization.dashboardVisualizationId, -1)
              }
              visualization={visualization}
            />
          ))}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            mt: 2,
            p: 3,
            textAlign: 'center'
          }}
        >
          <Typography fontWeight={700}>No graphs in this draft</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Add a graph to visualize how one or more metrics change over time.
          </Typography>
          {canEdit && (
            <Button
              disabled={!activeMetrics.length}
              onClick={openCreate}
              sx={{ mt: 2 }}
              variant="outlined"
            >
              Add first graph
            </Button>
          )}
        </Paper>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => !editorBusy && setEditorOpen(false)}
        open={editorOpen}
      >
        <Box component="form" noValidate onSubmit={submitVisualization}>
          <DialogTitle>
            {selectedVisualization ? 'Edit graph' : 'Add graph'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <GraphFields
                activeMetrics={activeMetrics}
                errors={errors}
                form={form}
                onChange={setFormValue}
                onMoveSeries={moveSeries}
                onSeriesChange={setSeriesValue}
                onToggleMetric={toggleMetric}
              />
              {(createState.error ||
                updateState.error ||
                seriesState.error) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(
                    createState.error || updateState.error || seriesState.error
                  )}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={editorBusy} onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button disabled={editorBusy} type="submit" variant="contained">
              {editorBusy
                ? 'Saving…'
                : selectedVisualization
                  ? 'Save graph'
                  : 'Add graph'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        maxWidth="sm"
        onClose={() => !deleteState.isLoading && setDeleteVisualization(null)}
        open={Boolean(deleteVisualization)}
      >
        <DialogTitle>Remove {deleteVisualization?.name}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This removes the graph from the current draft. It does not delete
            metric data, and the currently published dashboard remains unchanged
            until this draft is published.
          </Alert>
          {deleteState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(deleteState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteState.isLoading}
            onClick={() => setDeleteVisualization(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleteState.isLoading}
            onClick={confirmDelete}
            variant="contained"
          >
            {deleteState.isLoading ? 'Removing…' : 'Remove graph'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardVisualizationDesigner;
